import { Button, Form, Modal, Input, Divider, Row, message } from "antd";
import { CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  query,
  where,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

const FIRESTORE_BATCH_LIMIT = 450;

const commitInBatches = async (operations) => {
  for (let start = 0; start < operations.length; start += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(db);
    operations
      .slice(start, start + FIRESTORE_BATCH_LIMIT)
      .forEach((operation) => operation(batch));
    await batch.commit();
  }
};

export default function CloneModal({
  fetchData,
  refreshInventory,
  groupData,
  isMobile,
  onOpen,
}) {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const showModal = () => {
    setOpen(true);
    onOpen?.();
  };

  const createClone = async (value) => {
    let newGroupRef;
    let cloneActivated = false;

    try {
      setSaving(true);
      // ----------------------------
      // โหลด Group ต้นฉบับ
      // ----------------------------
      const sourceGroupRef = doc(db, "productGroups", groupData.key);

      const sourceGroupSnap = await getDoc(sourceGroupRef);

      if (!sourceGroupSnap.exists()) {
        throw new Error("Group not found");
      }

      const sourceGroup = sourceGroupSnap.data();
      // ----------------------------
      // สร้าง Group ใหม่
      // ----------------------------
      newGroupRef = await addDoc(collection(db, "productGroups"), {
        ...sourceGroup,

        name: value.name,

        cloneTo: null,
        status: "cloning",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      // ----------------------------
      // Clone Options
      // ----------------------------
      const optionsSnap = await getDocs(
        collection(db, "productGroups", groupData.key, "options"),
      );
      const productsByOption = await Promise.all(
        optionsSnap.docs.map((optionDoc) =>
          getDocs(
            collection(
              db,
              "productGroups",
              groupData.key,
              "options",
              optionDoc.id,
              "products",
            ),
          ),
        ),
      );

      const cloneOperations = [];
      optionsSnap.docs.forEach((optionDoc, index) => {
        cloneOperations.push((batch) =>
          batch.set(
            doc(db, "productGroups", newGroupRef.id, "options", optionDoc.id),
            optionDoc.data(),
          ),
        );
        productsByOption[index].docs.forEach((productDoc) => {
          cloneOperations.push((batch) =>
            batch.set(
              doc(
                db,
                "productGroups",
                newGroupRef.id,
                "options",
                optionDoc.id,
                "products",
                productDoc.id,
              ),
              productDoc.data(),
            ),
          );
        });
      });
      await commitInBatches(cloneOperations);

      // ----------------------------
      // Update Redirect Mapping
      // AAA -> CCC
      // BBB -> CCC
      // ----------------------------
      const redirectSnap = await getDocs(
        query(
          collection(db, "productGroups"),
          where("cloneTo", "==", groupData.key),
        ),
      );

      // เปิด Clone ใหม่และซ่อนต้นฉบับพร้อมกัน เพื่อลดโอกาสแสดงซ้ำ
      const cloneLinkBatch = writeBatch(db);
      cloneLinkBatch.update(doc(db, "productGroups", groupData.key), {
        cloneTo: newGroupRef.id,
        updatedAt: serverTimestamp(),
      });
      cloneLinkBatch.update(newGroupRef, {
        status: sourceGroup.status || "available",
        updatedAt: serverTimestamp(),
      });
      redirectSnap.forEach((item) => {
        cloneLinkBatch.update(doc(db, "productGroups", item.id), {
          cloneTo: newGroupRef.id,
          updatedAt: serverTimestamp(),
        });
      });
      await cloneLinkBatch.commit();
      cloneActivated = true;

      // ----------------------------
      // Delete Old Products
      // ----------------------------
      const cleanupOperations = productsByOption.flatMap((snapshot) =>
        snapshot.docs.map((productDoc) => (batch) =>
          batch.delete(productDoc.ref),
        ),
      );
      cleanupOperations.push(
        ...optionsSnap.docs.map((optionDoc) => (batch) =>
          batch.delete(optionDoc.ref),
        ),
      );
      await commitInBatches(cleanupOperations);

      form.resetFields();

      setOpen(false);

      await Promise.all([fetchData(null, 1), refreshInventory?.()]);

      message.success("Clone สินค้าสำเร็จ");
      console.log("Clone Success", newGroupRef.id);
    } catch (error) {
      console.error("Clone Product Error", error);
      if (newGroupRef && !cloneActivated) {
        try {
          await updateDoc(newGroupRef, {
            status: "clone_failed",
            cloneError: error?.message || "Clone ไม่สำเร็จ",
            updatedAt: serverTimestamp(),
          });
        } catch (statusError) {
          console.error("Update clone failed status error", statusError);
        }
      }
      message.error(
        cloneActivated
          ? "Clone สำเร็จ แต่ลบข้อมูลเดิมไม่ครบ กรุณาลองใหม่อีกครั้ง"
          : `Clone ไม่สำเร็จ: ${error?.message || "กรุณาลองใหม่"}`,
      );
      await Promise.all([fetchData(null, 1), refreshInventory?.()]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        onClick={showModal}
        className="neo-btn-update-option"
        icon={<CopyOutlined />}
        style={isMobile ? { width: "100%" } : {}}
      >
        Clone สินค้า
      </Button>

      {/* MODAL */}
      <Modal
        width={500}
        open={open}
        footer={false}
        closeIcon={false}
        maskClosable={!saving}
        keyboard={!saving}
        centered
        className="neo-modal"
      >
        <div className="neo-modal-header">
          <div>
            <div className="neo-modal-topline">MANAGEMENT</div>

            <div className="neo-modal-title">📦 โคลนข้อมูลสินค้า</div>
          </div>

          <Button
            className="neo-close-btn"
            disabled={saving}
            onClick={() => {
              setOpen(false);
              form?.resetFields();
            }}
          >
            ✕
          </Button>
        </div>

        <Divider
          style={{
            borderColor: "rgba(255,255,255,0.08)",
            margin: "18px 0px",
          }}
        />

        <Form form={form} layout="vertical" onFinish={createClone}>
          <div className="neo-form-label">ชื่อสินค้า</div>
          <Form.Item
            name={"name"}
            rules={[
              {
                required: true,
                message: "กรุณากรอกชื่อสินค้า",
              },
            ]}
          >
          <Input
            placeholder="ชื่อสินค้า"
            className="neo-input-modal"
            disabled={saving}
          />
          </Form.Item>

          {/* FOOTER BUTTON */}
          <Row justify="space-between" gutter={[12, 12]}>
            {isMobile ? null : (
              <Button
                className="neo-cancel-btn"
                loading={saving}
                disabled={saving}
                onClick={() => {
                  setOpen(false);
                  form?.resetFields();
                }}
              >
                ยกเลิก
              </Button>
            )}

            <Button
              style={isMobile ? { width: "100%" } : {}}
              htmlType="submit"
              className="neo-save-btn"
              loading={saving}
              disabled={saving}
            >
              บันทึก
            </Button>
          </Row>
        </Form>
      </Modal>

      {/* STYLE */}
      <style>{`
        /* OPEN BUTTON */
    
        .neo-btn-update-option{
          background:rgba(243, 246, 59, 0.15) !important;
          border:1px solid rgba(246, 234, 59, 0.3) !important;
          color:#f59e0b !important;
          font-weight:700;
          border-radius:10px;
        }

        .neo-btn-update-option:hover{
          transform:translateY(-2px);
        }

        /* MODAL */
        .neo-modal .ant-modal-content{
          background:
            linear-gradient(
              135deg,
              rgba(255,255,255,.14),
              rgba(255,255,255,.05)
            ) !important;

          border:1px solid rgba(255,255,255,.08);

          backdrop-filter:blur(26px);

          border-radius:30px;

          padding:26px;

          overflow:hidden;

          box-shadow:
            0 25px 60px rgba(0,0,0,.28);
        }

        .neo-modal .ant-modal-mask{
          backdrop-filter:blur(6px);
        }

        /* HEADER */
        .neo-modal-header{
          display:flex;
          justify-content:space-between;
          align-items:flex-start;
          gap:16px;
        }

        .neo-modal-topline{
          color:#fc950f;
          font-size:12px;
          font-weight:800;
          letter-spacing:3px;
        }

        .neo-modal-title{
          color:white;
          font-size:28px;
          font-weight:900;
          margin-top:4px;
        }

        .neo-close-btn{
          width:42px;
          height:42px;

          border:none !important;

          border-radius:14px !important;

          background:
            rgba(255,255,255,.08) !important;

          color:white !important;
        }

        /* SUBJECT CARD */
        .neo-subject-card{
          padding:18px;

          border-radius:22px;

          background:
            rgba(255,255,255,.05);

          border:1px solid rgba(255,255,255,.06);

          margin-bottom:16px;
        }

        .neo-subject-head{
          display:flex;
          justify-content:space-between;
          align-items:center;

          margin-bottom:14px;

          color:white;

          font-weight:800;
        }

        .neo-remove-icon{
          color:#f87171;
          font-size:18px;
          cursor:pointer;
        }

        /* INPUT */
        .neo-input-modal{
          height:52px !important;

          border:none !important;
          outline:none !important;

          border-radius:16px !important;

          background:
            rgba(255,255,255,.92) !important;

          font-weight:600;
        }

        /* SELECT */
        .neo-select .ant-select-selector{
          height:52px !important;

          border:none !important;

          border-radius:16px !important;

          background:
            rgba(255,255,255,.92) !important;

          display:flex;
          align-items:center;
        }

        /* ADD BTN */
        .neo-dashed-btn{
          width:100%;
          height:54px;

          border-radius:18px !important;

          background:
            rgba(255,255,255,.05) !important;

          border:1px dashed rgba(255,255,255,.15) !important;

          color:white !important;

          font-weight:700;
        }

        /* FOOTER BUTTON */
        .neo-cancel-btn{
          width:48%;
          height:54px;

          border:none !important;

          border-radius:18px !important;

          background:
            rgba(255,255,255,.08) !important;

          color:white !important;

          font-weight:700;
        }

        .neo-save-btn{
          width:48%;
          height:54px;

          border:none !important;

          border-radius:18px !important;

          background:
    linear-gradient(
      135deg,
      #fbbf24,
      #dc2626
    ) !important;

          color:white !important;

          font-weight:800;

          box-shadow:
            0 18px 45px rgba(220,38,38,.45);;
        }

        /* MOBILE */
        @media (max-width: 768px), (min-width: 769px) and (max-width: 1366px) and (pointer: coarse){

          .neo-modal .ant-modal-content{
            padding:20px;
            border-radius:24px;
          }

          .neo-modal-title{
            font-size:22px;
          }

          // .neo-cancel-btn,
          // .neo-save-btn{
          //   width:100%;
          // }
        }
      `}</style>
    </>
  );
}
